import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { errMsg } from '../components/diagnostico/errMsg.js';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { Checkbox } from '../components/ui/checkbox.jsx';
import {
  Plus, Stethoscope, Trash2, Edit, Play, Pause, Users, BarChart3,
  ChevronUp, ChevronDown, X, ClipboardList, Lock, CheckCircle2, Search, Copy, Link2, Unlock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const diagLink = (token) => `${window.location.origin}/d/${token}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
  toast.success('Link copiado');
};

const LinkCell = ({ perms, companyId }) => {
  if (!perms.can_answer) return <span className="w-24" />;
  if (!perms.token) return <span className="w-24 text-[10px] text-slate-400">salve p/ gerar</span>;
  return (
    <button onClick={() => copyText(diagLink(perms.token))} className="w-24 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800" data-testid={`copy-link-${companyId}`}>
      <Copy className="w-3.5 h-3.5" /> Copiar link
    </button>
  );
};

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const Q_TYPES = [
  { v: 'single', l: 'Escolha única' },
  { v: 'multiple', l: 'Múltipla escolha' },
  { v: 'open', l: 'Resposta aberta' },
];

const emptyQuestion = () => ({
  id: uid(),
  text: '',
  type: 'single',
  options: ['', ''],
  max_selections: null,
});

const Diagnosticos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = localStorage.getItem('userRole') || 'respondent';
  const isAdmin = userRole === 'admin' || userRole === 'manager';

  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [companies, setCompanies] = useState([]);

  // Builder modal
  const [showBuilder, setShowBuilder] = useState(false);
  const [editing, setEditing] = useState(null); // id | null (create)
  const [draft, setDraft] = useState({ nome: '', descricao: '', questions: [] });

  // Access modal
  const [accessModal, setAccessModal] = useState(null);
  const [accessState, setAccessState] = useState({}); // {company_id: {can_answer, can_see_results}}
  const [accessSearch, setAccessSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/diagnostics`, auth());
      setItems(data.items || []);
    } catch (e) { console.error(e); } finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) {
      axios.get(`${API}/companies/list`, auth())
        .then(r => setCompanies((r.data.companies || []).filter(c => c.role === 'respondent' || !c.role)))
        .catch(() => {});
    }
  }, [load, isAdmin]);

  // ====== BUILDER ======
  const openCreate = () => {
    setEditing(null);
    setDraft({ nome: '', descricao: '', questions: [emptyQuestion()] });
    setShowBuilder(true);
  };

  const openEdit = async (d) => {
    if (d.status === 'active') {
      toast.error('Inative o diagnóstico para editar.');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/diagnostics/${d.id}`, auth());
      setEditing(d.id);
      setDraft({
        nome: data.nome || '',
        descricao: data.descricao || '',
        questions: (data.questions || []).map(q => ({
          id: q.id || uid(),
          text: q.text || '',
          type: q.type || 'single',
          options: q.type === 'open' ? [] : (q.options?.length ? q.options : ['', '']),
          max_selections: q.max_selections ?? null,
        })),
      });
      setShowBuilder(true);
    } catch (err) { toast.error(errMsg(err, 'Erro ao carregar')); }
  };

  const addQuestion = () => setDraft(p => ({ ...p, questions: [...p.questions, emptyQuestion()] }));
  const removeQuestion = (idx) => setDraft(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, dir) => {
    setDraft(p => {
      const next = [...p.questions];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return p;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...p, questions: next };
    });
  };
  const updateQ = (idx, patch) => setDraft(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === idx ? { ...q, ...patch } : q),
  }));
  const updateOption = (qIdx, oIdx, val) => setDraft(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === qIdx ? { ...q, options: q.options.map((o, j) => j === oIdx ? val : o) } : q),
  }));
  const addOption = (qIdx) => setDraft(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === qIdx ? { ...q, options: [...q.options, ''] } : q),
  }));
  const removeOption = (qIdx, oIdx) => setDraft(p => ({
    ...p,
    questions: p.questions.map((q, i) => {
      if (i !== qIdx) return q;
      const options = q.options.filter((_, j) => j !== oIdx);
      let max_selections = q.max_selections;
      if (max_selections && max_selections > options.length) max_selections = options.length || null;
      return { ...q, options, max_selections };
    }),
  }));

  const saveDraft = async () => {
    if (!draft.nome.trim()) { toast.error('Informe o nome do diagnóstico'); return; }
    const invalid = draft.questions.find(q => !q.text.trim() || (q.type !== 'open' && q.options.filter(o => o.trim()).length < 2));
    if (invalid) { toast.error('Cada pergunta precisa de texto e, para escolhas, ao menos 2 opções não vazias.'); return; }
    const payload = {
      nome: draft.nome.trim(),
      descricao: draft.descricao,
      questions: draft.questions.map(q => ({
        id: q.id,
        text: q.text,
        type: q.type,
        options: q.type === 'open' ? [] : q.options.filter(o => o.trim()),
        max_selections: q.type === 'multiple' ? q.max_selections : null,
      })),
    };
    try {
      if (editing) {
        await axios.put(`${API}/diagnostics/${editing}`, payload, auth());
        toast.success('Diagnóstico atualizado');
      } else {
        await axios.post(`${API}/diagnostics`, payload, auth());
        toast.success('Diagnóstico criado (inativo). Libere o acesso e ative para começar a receber respostas.');
      }
      setShowBuilder(false);
      load();
    } catch (err) { toast.error(errMsg(err, 'Erro ao salvar')); }
  };

  useEffect(() => {
    if (isAdmin && searchParams.get('novo') === '1') {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, searchParams]);

  // ====== STATUS ======
  const toggleStatus = async (d) => {
    const next = d.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(`${API}/diagnostics/${d.id}/status`, { status: next }, auth());
      toast.success(next === 'active' ? 'Diagnóstico ativado' : 'Diagnóstico inativado');
      load();
    } catch (err) { toast.error(errMsg(err, 'Erro')); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Excluir diagnóstico "${d.nome}"? Todas as respostas também serão apagadas.`)) return;
    try {
      await axios.delete(`${API}/diagnostics/${d.id}`, auth());
      toast.success('Diagnóstico excluído');
      load();
    } catch (err) { toast.error(errMsg(err, 'Erro')); }
  };

  // ====== ACCESS ======
  const openAccess = async (d) => {
    try {
      const { data } = await axios.get(`${API}/diagnostics/${d.id}`, auth());
      setAccessState(data.access || {});
      setAccessModal(d);
      setAccessSearch('');
    } catch (err) { toast.error(errMsg(err, 'Erro')); }
  };
  const saveAccess = async () => {
    try {
      const { data } = await axios.put(`${API}/diagnostics/${accessModal.id}/access`, { access: accessState }, auth());
      setAccessState(data.access || {});
      toast.success('Acessos salvos. Copie os links para enviar às empresas.');
      load();
    } catch (err) { toast.error(errMsg(err, 'Erro')); }
  };
  const setAllAccess = (field, value) => setAccessState(prev => {
    const next = { ...prev };
    const ids = field === 'can_answer' ? filteredCompanies.map(c => c.id) : Object.keys(prev).filter(id => prev[id]?.can_answer);
    ids.forEach(id => {
      const cur = next[id] || { can_answer: false, can_see_results: false };
      const upd = { ...cur, [field]: value };
      if (field === 'can_answer' && !value) upd.can_see_results = false;
      next[id] = upd;
    });
    return next;
  });
  const toggleAccess = (companyId, field) => setAccessState(prev => {
    const cur = prev[companyId] || { can_answer: false, can_see_results: false };
    const next = { ...cur, [field]: !cur[field] };
    // Se marca can_see_results, auto-marca can_answer (precisa ter respondido ou ter acesso)
    if (field === 'can_see_results' && next.can_see_results) next.can_answer = true;
    // Se desmarca can_answer, desmarca can_see_results também
    if (field === 'can_answer' && !next.can_answer) next.can_see_results = false;
    return { ...prev, [companyId]: next };
  });

  const filteredCompanies = companies.filter(c =>
    !accessSearch || (c.razao_social || '').toLowerCase().includes(accessSearch.toLowerCase())
  );

  const stats = {
    total: items.length,
    active: items.filter(i => i.status === 'active').length,
    inactive: items.filter(i => i.status !== 'active').length,
    responses: items.reduce((s, i) => s + (i.response_count || 0), 0),
  };

  // ====== RESPONDENT VIEW ======
  if (!isAdmin) {
    return (
      <MainLayout title="Diagnósticos" userRole={userRole}>
        <div className="flex flex-col gap-4" data-testid="diagnostics-respondent">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-5 text-white">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Stethoscope className="w-5 h-5" /> Diagnósticos liberados para você</h2>
            <p className="text-xs opacity-70 mt-1">Responda os diagnósticos abaixo. Suas respostas são confidenciais e agregadas no benchmark.</p>
          </div>
          {!loaded ? (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="diag-loading">Carregando...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">Nenhum diagnóstico liberado no momento.</p>
          ) : (
            <div className="grid gap-3">
              {items.map(d => (
                <Card key={d.id} data-testid={`diag-card-${d.id}`}>
                  <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <h3 className="font-semibold text-slate-900">{d.nome}</h3>
                      {d.descricao && <p className="text-sm text-slate-500 mt-1">{d.descricao}</p>}
                      <p className="text-[11px] text-slate-400 mt-2">
                        {d.questions?.length || 0} perguntas
                        {d.already_answered && (<> · <span className="text-emerald-600 font-medium">Respondido em {new Date(d.answered_at).toLocaleDateString('pt-BR')}</span></>)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/diagnosticos/${d.id}/responder`)}
                        data-testid={`responder-${d.id}`}
                      >
                        <ClipboardList className="w-3.5 h-3.5 mr-1" />
                        {d.can_see_results ? (d.already_answered ? 'Ver Respostas' : 'Encerrado') : (d.already_answered ? 'Editar Respostas' : 'Responder')}
                      </Button>
                      {d.can_see_results && (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/diagnosticos/${d.id}/resultados`)} data-testid={`result-${d.id}`}>
                          <BarChart3 className="w-3.5 h-3.5 mr-1" /> Resultados
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </MainLayout>
    );
  }

  // ====== ADMIN / MANAGER VIEW ======
  return (
    <MainLayout title="Diagnósticos" userRole={userRole}>
      <div className="space-y-6" data-testid="diagnostics-admin">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-4 text-white">
            <p className="text-[11px] opacity-60 flex items-center gap-1"><Stethoscope className="w-3 h-3" /> Total</p>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-[11px] text-emerald-600">Ativos</p>
            <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
          </div>
          <div className="bg-slate-100 rounded-xl p-4">
            <p className="text-[11px] text-slate-500">Inativos</p>
            <p className="text-2xl font-bold text-slate-700">{stats.inactive}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-[11px] text-blue-600">Respostas Coletadas</p>
            <p className="text-2xl font-bold text-blue-700">{stats.responses}</p>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Stethoscope className="w-4 h-4 text-blue-600" /> Diagnósticos</CardTitle>
            <Button onClick={openCreate} data-testid="new-diag-btn"><Plus className="w-4 h-4 mr-1" /> Novo Diagnóstico</Button>
          </CardHeader>
          <CardContent>
            {!loaded ? (
              <p className="text-sm text-slate-400 text-center py-8" data-testid="diag-loading">Carregando...</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nenhum diagnóstico criado ainda.</p>
            ) : (
              <div className="space-y-3">
                {items.map(d => {
                  const isActive = d.status === 'active';
                  return (
                    <div key={d.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-testid={`diag-row-${d.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{d.nome}</h3>
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700" data-testid={`status-active-${d.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Ativo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600" data-testid={`status-inactive-${d.id}`}>
                                <Pause className="w-3 h-3" /> Inativo
                              </span>
                            )}
                          </div>
                          {d.descricao && <p className="text-sm text-slate-500 mt-1">{d.descricao}</p>}
                          <p className="text-[11px] text-slate-400 mt-2">
                            {d.questions?.length || 0} perguntas · {d.authorized_count || 0} empresas autorizadas · {d.response_count || 0} respostas · {d.released_count || 0} com resultados liberados
                            {d.activated_at && isActive && <> · Ativado em {new Date(d.activated_at).toLocaleDateString('pt-BR')}</>}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant={isActive ? 'outline' : 'default'} onClick={() => toggleStatus(d)} data-testid={`toggle-${d.id}`}>
                            {isActive ? <><Pause className="w-3.5 h-3.5 mr-1" /> Inativar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Ativar</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openAccess(d)} data-testid={`access-${d.id}`}>
                            <Users className="w-3.5 h-3.5 mr-1" /> Gerenciar Acesso
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(d)} disabled={isActive} data-testid={`edit-${d.id}`} title={isActive ? 'Inative para editar' : 'Editar'}>
                            {isActive ? <Lock className="w-3.5 h-3.5 mr-1" /> : <Edit className="w-3.5 h-3.5 mr-1" />}
                            Editar
                          </Button>
                          <Link to={`/diagnosticos/${d.id}/resultados`}>
                            <Button size="sm" variant="outline" data-testid={`result-${d.id}`}>
                              <BarChart3 className="w-3.5 h-3.5 mr-1" /> Resultados
                            </Button>
                          </Link>
                          <button onClick={() => remove(d)} className="text-slate-400 hover:text-red-500" data-testid={`delete-${d.id}`} title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ========== BUILDER MODAL ========== */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="builder-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Diagnóstico' : 'Novo Diagnóstico'}</DialogTitle>
            <DialogDescription>Monte o questionário. Após salvar, o diagnóstico fica inativo até você liberar o acesso e ativá-lo.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Nome do Diagnóstico *</Label>
                <Input value={draft.nome} onChange={e => setDraft(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Diagnóstico de Clima 2026" data-testid="draft-nome" />
              </div>
              <div>
                <Label className="text-xs">Descrição (opcional)</Label>
                <Textarea value={draft.descricao} onChange={e => setDraft(p => ({ ...p, descricao: e.target.value }))} rows={2} placeholder="Contexto do diagnóstico" data-testid="draft-descricao" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700">Perguntas ({draft.questions.length})</h4>
                <Button size="sm" onClick={addQuestion} data-testid="add-question-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Adicionar Pergunta</Button>
              </div>

              {draft.questions.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6 border border-dashed rounded-lg">Nenhuma pergunta ainda. Clique em "Adicionar Pergunta".</p>
              )}

              <div className="space-y-3">
                {draft.questions.map((q, qIdx) => (
                  <div key={q.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`question-${qIdx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500">Pergunta {qIdx + 1}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Subir" data-testid={`up-${qIdx}`}><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={() => moveQuestion(qIdx, 1)} disabled={qIdx === draft.questions.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Descer" data-testid={`down-${qIdx}`}><ChevronDown className="w-4 h-4" /></button>
                        <button onClick={() => removeQuestion(qIdx)} className="p-1 text-slate-400 hover:text-red-500" title="Remover" data-testid={`remove-q-${qIdx}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <Label className="text-xs">Texto da pergunta *</Label>
                        <Input value={q.text} onChange={e => updateQ(qIdx, { text: e.target.value })} placeholder="Ex: Como você avalia o ambiente de trabalho?" data-testid={`q-text-${qIdx}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Tipo de resposta</Label>
                        <Select value={q.type} onValueChange={v => updateQ(qIdx, { type: v, options: v === 'open' ? [] : (q.options.length < 2 ? ['', ''] : q.options), max_selections: v === 'multiple' ? q.max_selections : null })}>
                          <SelectTrigger data-testid={`q-type-${qIdx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Q_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    {q.type !== 'open' && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs">Opções</Label>
                          {q.type === 'multiple' && (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Máx. seleções:</Label>
                              <Input
                                type="number"
                                min={1}
                                max={q.options.length || 1}
                                value={q.max_selections || ''}
                                onChange={e => updateQ(qIdx, { max_selections: e.target.value ? Math.max(1, Math.min(parseInt(e.target.value) || 1, q.options.length)) : null })}
                                placeholder="sem limite"
                                className="w-24 h-8 text-xs"
                                data-testid={`q-maxsel-${qIdx}`}
                              />
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {q.options.map((o, oIdx) => (
                            <div key={oIdx} className="flex items-center gap-2">
                              <span className="text-xs text-slate-400 w-6">{oIdx + 1}.</span>
                              <Input
                                value={o}
                                onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                                placeholder={`Opção ${oIdx + 1}`}
                                className="flex-1"
                                data-testid={`q-opt-${qIdx}-${oIdx}`}
                              />
                              <button
                                onClick={() => removeOption(qIdx, oIdx)}
                                disabled={q.options.length <= 2}
                                className="text-slate-400 hover:text-red-500 disabled:opacity-30"
                                data-testid={`q-rm-opt-${qIdx}-${oIdx}`}
                                title="Remover opção"
                              ><X className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => addOption(qIdx)} data-testid={`add-opt-${qIdx}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar opção
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowBuilder(false)} data-testid="cancel-builder">Cancelar</Button>
              <Button onClick={saveDraft} data-testid="save-builder">{editing ? 'Atualizar' : 'Criar Diagnóstico'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== ACCESS MODAL ========== */}
      <Dialog open={!!accessModal} onOpenChange={(o) => !o && setAccessModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="access-modal">
          <DialogHeader>
            <DialogTitle>Gerenciar Acesso — {accessModal?.nome}</DialogTitle>
            <DialogDescription>
              Marque as empresas que podem responder. Cada uma recebe um link exclusivo, sem necessidade de cadastro ou login. Ao liberar os resultados para uma empresa, as respostas dela ficam bloqueadas para edição.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={accessSearch} onChange={e => setAccessSearch(e.target.value)} placeholder="Buscar empresa..." className="pl-8" data-testid="access-search" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setAllAccess('can_answer', true)} data-testid="authorize-all"><Link2 className="w-3.5 h-3.5 mr-1" /> Autorizar todas (filtro)</Button>
              <Button size="sm" variant="outline" onClick={() => setAllAccess('can_see_results', true)} data-testid="release-all"><Unlock className="w-3.5 h-3.5 mr-1" /> Liberar resultados a todas autorizadas</Button>
              <Button size="sm" variant="outline" onClick={() => setAllAccess('can_see_results', false)} data-testid="unrelease-all"><Lock className="w-3.5 h-3.5 mr-1" /> Ocultar resultados de todas</Button>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500 px-2">
              <span>{filteredCompanies.length} empresas</span>
              <div className="flex gap-6 pr-2">
                <span className="font-semibold">Responder</span>
                <span className="font-semibold">Ver Resultados</span>
                <span className="font-semibold w-24">Link</span>
              </div>
            </div>
            <div className="divide-y border rounded-lg max-h-[50vh] overflow-y-auto">
              {filteredCompanies.map(c => {
                const perms = accessState[c.id] || {};
                return (
                  <div key={c.id} className="flex items-center justify-between p-2 hover:bg-slate-50" data-testid={`access-row-${c.id}`}>
                    <span className="text-sm truncate flex-1 pr-2">{c.razao_social}</span>
                    <div className="flex items-center gap-6 pr-2">
                      <span className="w-[58px] flex justify-center"><Checkbox
                        checked={!!perms.can_answer}
                        onCheckedChange={() => toggleAccess(c.id, 'can_answer')}
                        data-testid={`access-answer-${c.id}`}
                      /></span>
                      <span className="w-[86px] flex justify-center"><Checkbox
                        checked={!!perms.can_see_results}
                        onCheckedChange={() => toggleAccess(c.id, 'can_see_results')}
                        data-testid={`access-results-${c.id}`}
                      /></span>
                      <LinkCell perms={perms} companyId={c.id} />
                    </div>
                  </div>
                );
              })}
              {filteredCompanies.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">Nenhuma empresa encontrada.</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setAccessModal(null)} data-testid="close-access">Fechar</Button>
              <Button onClick={saveAccess} data-testid="save-access">Salvar Acessos</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Diagnosticos;
